import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiErrorMessage } from '../../lib/api';
import { economicsAPI } from '../../lib/economicsApi';
import { formatDate } from '../../lib/formats';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';

/**
 * Réglage du « Début de l'exercice » — le mois où commence l'année comptable de la
 * société (janvier par défaut). Il sert au bilan mensuel (cumul depuis le début de
 * l'exercice). Réservé à l'administrateur de la société.
 */

const NOMS_DES_MOIS = Array.from({ length: 12 }, (_, index) =>
  new Date(2000, index, 1).toLocaleDateString('fr-FR', { month: 'long' }),
);

export const optionsDesMois = () =>
  NOMS_DES_MOIS.map((nom, index) => ({
    value: String(index + 1),
    label: nom.charAt(0).toUpperCase() + nom.slice(1),
  }));

export default function FiscalYearSetting() {
  const { canSeeMoney, isAdminSociete } = useAuth();
  const autorise = Boolean(canSeeMoney && isAdminSociete);

  const [vue, setVue] = useState(null);
  const [mois, setMois] = useState('1');
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const appliquer = useCallback((reponse) => {
    setVue(reponse);
    setMois(String(reponse?.start_month || 1));
  }, []);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      appliquer(await economicsAPI.getFiscalYear());
    } catch (error) {
      setErreur(apiErrorMessage(error, "Impossible de lire le début de l'exercice."));
    }
  }, [appliquer]);

  useEffect(() => {
    if (autorise) charger();
  }, [autorise, charger]);

  const options = useMemo(optionsDesMois, []);

  if (!autorise) return null;

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      appliquer(await economicsAPI.saveFiscalYear(mois));
      toast.success("Début de l'exercice enregistré");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Impossible d'enregistrer le début de l'exercice."));
    } finally {
      setEnCours(false);
    }
  };

  const inchange = vue && String(vue.start_month) === mois;

  return (
    <form onSubmit={enregistrer} className="space-y-3" data-testid="debut-exercice">
      {erreur && (
        <p className="text-sm text-destructive" role="alert">
          {erreur}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full max-w-xs space-y-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="debut-exercice-mois" className="text-sm font-medium">
              Début de l'exercice
            </label>
            <Aide titre="Début de l'exercice">
              Le mois où commence votre année comptable. Le bilan mensuel cumule les chiffres depuis ce mois.
            </Aide>
          </div>
          <SearchableSelect
            id="debut-exercice-mois"
            value={mois}
            onValueChange={setMois}
            options={options}
            placeholder="Choisir le mois"
            searchPlaceholder="Rechercher un mois…"
            emptyMessage="Aucun mois trouvé"
          />
        </div>
        <Button type="submit" disabled={enCours || Boolean(inchange)}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
      {vue?.current && (
        <p className="text-sm" data-testid="exercice-en-cours">
          {vue.current.label}: du {formatDate(vue.current.start)} au {formatDate(vue.current.end)}.
        </p>
      )}
    </form>
  );
}
